/**
 * Termly CMP adapter — the ONLY module that touches the vendor's browser
 * globals (`window.__tcfapi`, `window.__gpp`, `window.Termly`) and the Global
 * Privacy Control signal. Everything else in the app reads tracking consent
 * through these helpers, so swapping CMP vendors is a one-file change.
 *
 * All access is feature-detected and wrapped in try/catch: Termly only loads on
 * the signed-out home page and the cookie-preferences page (see TermlyLoader),
 * so on every other route these helpers degrade to "denied/unknown" rather than
 * throwing — mirroring the best-effort storage pattern in
 * `src/agents/preferences.ts`.
 *
 * This is the TRACKING-consent layer. The account-level AGREEMENT consent
 * (Terms / Privacy / AI Notice) is a separate system in `src/auth/useConsent.ts`
 * and is never read or written here. See `docs/privacy-consent.md`.
 */

/** Termly property (resource-blocker) UUID. Public value, safe to ship. */
const TERMLY_PROPERTY_ID = 'f66aa98f-2ca8-46b5-b100-83b2a8632903';

/** id stamped on the injected <script> so we can find/remove it. */
const TERMLY_SCRIPT_ID = 'om-termly-resource-blocker';

const TERMLY_SRC = `https://app.termly.io/resource-blocker/${TERMLY_PROPERTY_ID}?autoBlock=on`;

/** Normalised, vendor-neutral consent purposes for future tracker-gating. */
export interface ConsentPurposes {
  readonly functional: boolean;
  readonly analytics: boolean;
  readonly advertising: boolean;
}

/** Safe default: only strictly-necessary/functional storage is assumed. */
export const DENY_ALL: ConsentPurposes = {
  functional: true,
  analytics: false,
  advertising: false,
};

/** True when the browser/extension is emitting a Global Privacy Control signal. */
export function readGpc(): boolean {
  try {
    return navigator.globalPrivacyControl === true;
  } catch {
    return false;
  }
}

/** Is our Termly <script> already in the document? */
export function isTermlyScriptPresent(): boolean {
  try {
    return document.getElementById(TERMLY_SCRIPT_ID) !== null;
  } catch {
    return false;
  }
}

/** Inject the Termly resource-blocker into <head> (idempotent, best-effort). */
export function injectTermly(): void {
  try {
    if (isTermlyScriptPresent()) {
      return;
    }
    const script = document.createElement('script');
    script.id = TERMLY_SCRIPT_ID;
    script.src = TERMLY_SRC;
    document.head.appendChild(script);
  } catch {
    // Best-effort: a failed inject must not break the page.
  }
}

/**
 * Best-effort teardown: remove our injected script and any banner/preference
 * DOM Termly mounted, so nothing lingers visually after the user leaves the
 * signed-out home page. A third-party script can't be fully "unloaded" from
 * memory mid-session; this only keeps the UI clean (see plan risks). App-owned
 * `.termly-display-preferences` trigger elements are left alone.
 */
export function removeTermly(): void {
  try {
    document.getElementById(TERMLY_SCRIPT_ID)?.remove();
    document
      .querySelectorAll('[id^="termly-"], [class*="termly-styles-"]')
      .forEach((el) => {
        if (!el.classList.contains('termly-display-preferences')) {
          el.remove();
        }
      });
  } catch {
    // Best-effort.
  }
}

/** Termly SDK present? Gates the auto-open on the preferences page. */
export function isTermlyReady(): boolean {
  try {
    return (
      typeof window.Termly !== 'undefined' ||
      typeof window.displayPreferenceModal === 'function'
    );
  } catch {
    return false;
  }
}

/**
 * Open Termly's consent-preferences modal. Tries the SDK API first, then falls
 * back to clicking an app-rendered `.termly-display-preferences` element (which
 * Termly wires via a delegated listener). Returns whether a trigger fired.
 */
export function openTermlyPreferences(): boolean {
  try {
    if (typeof window.displayPreferenceModal === 'function') {
      window.displayPreferenceModal();
      return true;
    }
    if (typeof window.Termly?.displayPreferenceModal === 'function') {
      window.Termly.displayPreferenceModal();
      return true;
    }
    const link = document.querySelector<HTMLElement>(
      '.termly-display-preferences',
    );
    if (link) {
      link.click();
      return true;
    }
  } catch {
    // Best-effort.
  }
  return false;
}

/** TCF purpose ids we map onto the coarse advertising / analytics buckets. */
const AD_PURPOSES = [2, 3, 4];
const ANALYTICS_PURPOSES = [7, 8, 9];

interface TcData {
  gdprApplies?: boolean;
  eventStatus?: string;
  purpose?: { consents?: Record<string, boolean> };
}

function anyConsented(
  consents: Record<string, boolean> | undefined,
  ids: number[],
): boolean {
  if (!consents) {
    return false;
  }
  return ids.some((id) => consents[String(id)]);
}

/**
 * Read current tracking consent as a normalised purpose map, for gating future
 * ad/analytics tags (none exist today — see plan Phase 4). Resolves to
 * {@link DENY_ALL} when the CMP is absent or doesn't answer within `timeoutMs`.
 * An active GPC signal always forces `advertising` off.
 *
 * NOTE: the TCF→purpose mapping is intentionally coarse; refine it when a real
 * tracker is wired up.
 */
export function readConsentPurposes(timeoutMs = 1500): Promise<ConsentPurposes> {
  return new Promise((resolve) => {
    const gpc = readGpc();
    let settled = false;
    const finish = (p: ConsentPurposes): void => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(gpc ? { ...p, advertising: false } : p);
    };

    const timer = setTimeout(() => finish(DENY_ALL), timeoutMs);

    try {
      if (typeof window.__tcfapi !== 'function') {
        clearTimeout(timer);
        finish(DENY_ALL);
        return;
      }
      window.__tcfapi('getTCData', 2, (data, success) => {
        clearTimeout(timer);
        if (!success || typeof data !== 'object' || data === null) {
          finish(DENY_ALL);
          return;
        }
        const tc = data as TcData;
        // GDPR not applicable (e.g. a US visitor): TCF consent is N/A; rely on
        // GPP/GPC. Default to allow unless GPC opts the user out (handled above).
        if (tc.gdprApplies === false) {
          finish({ functional: true, analytics: true, advertising: true });
          return;
        }
        const consents = tc.purpose?.consents;
        finish({
          functional: true,
          analytics: anyConsented(consents, ANALYTICS_PURPOSES),
          advertising: anyConsented(consents, AD_PURPOSES),
        });
      });
    } catch {
      clearTimeout(timer);
      finish(DENY_ALL);
    }
  });
}
